import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  Users, GraduationCap, DollarSign, FilePlus, ArrowUpRight, LogOut, CheckCircle2, Clock, MessageCircle, Eye,
} from 'lucide-react';
import ChatPanel from '../components/ChatPanel';
import RecruiterInvoiceUpload from '../components/RecruiterInvoiceUpload';
import RecruiterStudentManage from '../components/RecruiterStudentManage';
import BookAppointment from '../components/BookAppointment';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { fmtDate } from '../lib/dateFmt';
import { StagePill } from '../lib/pipeline';
import CollegeListView from '../components/CollegeListView';

function RecruiterColleges({ onBack }) {
  const [colleges, setColleges] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/recruiter/colleges`, { credentials: 'include' });
        if (res.ok) setColleges((await res.json()).colleges || []);
        else setColleges([]);
      } catch { setColleges([]); }
    })();
  }, []);
  return (
    <div className="space-y-3" data-testid="recruiter-colleges-view">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-tge-navy/70 hover:text-tge-navy px-3 h-9 rounded-full border border-tge-navy/10 hover:bg-white transition"
        data-testid="recruiter-colleges-back"
      >
        ← Back to Dashboard
      </button>
      {colleges === null ? (
        <div className="text-center py-10 text-sm text-tge-muted">Loading colleges…</div>
      ) : (
        <CollegeListView colleges={colleges} readOnly showCommissions={false} />
      )}
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 0.9, 0.32, 1] },
  }),
};

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recruiter, setRecruiter] = useState(null);
  const [students, setStudents] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [commissionStructure, setCommissionStructure] = useState([]);
  const [error, setError] = useState('');
  const [me, setMe] = useState(null);
  const [view, setView] = useState('overview');
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (meRes.ok) setMe(await meRes.json());

        const dashRes = await fetch(`${API}/api/recruiter/dashboard`, { credentials: 'include' });
        if (!dashRes.ok) {
          if (dashRes.status === 401) return navigate('/recruiter-login');
          throw new Error('Failed to load dashboard');
        }
        const dash = await dashRes.json();
        setRecruiter(dash.recruiter);

        const [stuRes, comRes, structRes] = await Promise.all([
          fetch(`${API}/api/recruiter/students`, { credentials: 'include' }),
          fetch(`${API}/api/recruiter/commissions`, { credentials: 'include' }),
          fetch(`${API}/api/recruiter/commission-structure`, { credentials: 'include' }),
        ]);
        if (stuRes.ok) setStudents((await stuRes.json()).students || []);
        if (comRes.ok) {
          const cd = await comRes.json();
          setCommissions(cd.rows || []);
          setCommissionSummary(cd.summary || null);
        }
        if (structRes.ok) {
          const sd = await structRes.json();
          setCommissionStructure(sd.commissions || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Poll unread for header badge
  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/chat/unread`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (mounted) setUnreadTotal(d.total || 0);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  async function handleLogout() {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-tge-paper flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tge-navy" />
      </div>
    );
  }

  const inPipelineCount = students.filter((s) => s.pipeline_stage).length;
  const commissionReceivedCount = students.filter((s) => s.pipeline_stage === 'commission_received').length;
  const series = buildSeries(students, 14);

  return (
    <div className="min-h-screen bg-tge-paper" data-testid="recruiter-dashboard">
      <header className="bg-white/70 backdrop-blur-md border-b border-tge-navy/5 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setView('overview')}
            className="text-left hover:opacity-80 transition"
            title="Go to Dashboard"
            data-testid="recruiter-dashboard-home"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-tge-gold font-semibold">Recruiter Portal</div>
            <h1 className="font-serif-display text-2xl text-tge-navy mt-0.5">
              {view === 'messages' ? 'Messages' : view === 'colleges' ? 'Colleges & Programs' : `Welcome back${recruiter?.company_name ? `, ${recruiter.company_name}` : ''}`}
            </h1>
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => setView(view === 'colleges' ? 'overview' : 'colleges')}
              className="border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white transition"
              data-testid="recruiter-colleges-toggle"
            >
              <GraduationCap size={16} /> Colleges
            </button>
            <button
              onClick={() => setView(view === 'messages' ? 'overview' : 'messages')}
              className="relative border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white transition"
              data-testid="recruiter-messages-toggle"
            >
              <MessageCircle size={16} /> Messages
              {unreadTotal > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center font-semibold">
                  {unreadTotal}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/create-student')}
              className="bg-tge-gold hover:bg-tge-gold-soft text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 transition shadow-sm"
            >
              <FilePlus size={16} /> New student
            </button>
            <button
              onClick={handleLogout}
              className="border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white transition"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">{error}</div>
        )}

        {view === 'colleges' ? (
          <RecruiterColleges onBack={() => setView('overview')} />
        ) : view === 'messages' ? (
          <div className="space-y-3">
            <button
              onClick={() => setView('overview')}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-tge-navy/70 hover:text-tge-navy px-3 h-9 rounded-full border border-tge-navy/10 hover:bg-white transition"
              data-testid="recruiter-back-to-dashboard"
            >
              ← Back to Dashboard
            </button>
            <ChatPanel canInitiate={true} currentUserId={me?.id} onUnreadChange={setUnreadTotal} />
          </div>
        ) : (
          <RecruiterOverview
            students={students}
            setStudents={setStudents}
            commissions={commissions}
            commissionSummary={commissionSummary}
            commissionStructure={commissionStructure}
            recruiter={recruiter}
            submittedCount={inPipelineCount}
            approvedCount={commissionReceivedCount}
            series={series}
            navigate={navigate}
          />
        )}
      </main>
    </div>
  );
}

function RecruiterOverview({ students, setStudents, commissions, commissionSummary, commissionStructure, recruiter, submittedCount, approvedCount, series, navigate }) {
  const [selectedStudent, setSelectedStudent] = useState(null);
  return (
    <>
      {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { l: 'Total students', v: students.length, Icon: Users, color: '#1b2a6b', delta: '+8%' },
            { l: 'In pipeline', v: submittedCount, Icon: Clock, color: '#c8a035', delta: 'staff updated' },
            { l: 'Commission received', v: approvedCount, Icon: CheckCircle2, color: '#5fb487', delta: 'stage' },
            { l: 'Commissions tracked', v: commissions.length, Icon: DollarSign, color: '#3a4d9e', delta: 'active' },
          ].map((s, i) => (
            <motion.div
              key={s.l}
              custom={i}
              initial="hidden"
              animate="show"
              variants={fadeUp}
              className="bg-white rounded-2xl p-5 border border-tge-navy/5"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15`, color: s.color }}>
                  <s.Icon size={18} />
                </div>
                <span className="text-[11px] font-semibold text-tge-muted">{s.delta}</span>
              </div>
              <div className="font-serif-display text-3xl text-tge-navy mt-5">{s.v}</div>
              <div className="text-[11px] uppercase tracking-wider text-tge-muted mt-1">{s.l}</div>
            </motion.div>
          ))}
        </div>

        {/* Activity chart + Profile */}
        <div className="grid lg:grid-cols-3 gap-4">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="lg:col-span-2 bg-white rounded-2xl p-6 border border-tge-navy/5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-tge-muted">Last 14 days</div>
                <h3 className="font-serif-display text-2xl text-tge-navy mt-1">Submissions</h3>
              </div>
              <ArrowUpRight className="text-tge-gold" size={20} />
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={series} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="recArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c8a035" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#c8a035" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e3d5" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b6f7a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b6f7a' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }} />
                <Area type="monotone" dataKey="Students" stroke="#c8a035" strokeWidth={2.5} fill="url(#recArea)" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {recruiter && (
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="bg-white rounded-2xl p-6 border border-tge-navy/5">
              <div className="text-[11px] uppercase tracking-wider text-tge-muted">Your profile</div>
              <h3 className="font-serif-display text-2xl text-tge-navy mt-1 mb-4">{recruiter.company_name}</h3>
              <dl className="space-y-3 text-sm">
                <ProfileRow label="Recruiter ID" value={<span className="font-mono">{recruiter.recruiter_id}</span>} />
                <ProfileRow label="Status" value={
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium capitalize">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {recruiter.status}
                  </span>
                } />
                <ProfileRow label="Member since" value={fmtDate(recruiter.created_at)} />
              </dl>
            </motion.div>
          )}
        </div>

        {/* Recent students */}
        <motion.section variants={fadeUp} initial="hidden" animate="show" custom={3} className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between">
            <h3 className="font-serif-display text-xl text-tge-navy">Recent students</h3>
            <button
              onClick={() => navigate('/create-student')}
              className="text-tge-gold font-semibold text-sm hover:underline inline-flex items-center gap-1"
            >
              + Add student
            </button>
          </div>
          {students.length === 0 ? (
            <div className="text-center py-16 text-tge-muted text-sm">
              <Users className="mx-auto text-tge-cream" size={36} />
              <p className="mt-3">No student profiles yet. Click <strong className="text-tge-navy">+ Add student</strong> to create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-tge-cream-soft">
                  <tr>
                    {['Student', 'Email', 'Status', 'Created'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-tge-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-tge-cream">
                  {students.slice(0, 8).map((s) => (
                    <tr
                      key={s.student_id}
                      className="hover:bg-tge-cream-soft/50 transition cursor-pointer"
                      onClick={() => setSelectedStudent(s)}
                      data-testid={`recruiter-student-row-${s.student_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-tge-navy/10 text-tge-navy flex items-center justify-center font-serif-display">
                            {(s.student_name || '?').charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-tge-navy text-sm">{s.student_name}</div>
                            <div className="text-xs text-tge-muted">{s.student_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-tge-muted">{s.email}</td>
                      <td className="px-6 py-4">
                        {s.pipeline_stage
                          ? <StagePill stage={s.pipeline_stage} testId={`rec-row-stage-${s.student_id}`} />
                          : <span className="text-xs text-tge-navy/30">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-tge-muted flex items-center gap-2">
                        <span>{s.created_at ? fmtDate(s.created_at) : '—'}</span>
                        <Eye size={14} className="ml-auto text-tge-navy/30" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.section>

        {/* My Commissions */}
        <MyCommissionsSection commissions={commissions} summary={commissionSummary} />

        {/* My commission structure — read-only, set by TGE admin/staff */}
        <MyCommissionStructureSection rows={commissionStructure} />

        {/* Student detail dialog (opened by clicking a table row) */}
        <Dialog open={!!selectedStudent} onOpenChange={(o) => !o && setSelectedStudent(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recruiter-student-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-6">
                <span>{selectedStudent?.student_name}</span>
                <span className="flex items-center gap-2">
                  {selectedStudent?.pipeline_stage && <StagePill stage={selectedStudent.pipeline_stage} testId="rec-dialog-stage-pill" />}
                </span>
              </DialogTitle>
              <DialogDescription className="sr-only">Student application details</DialogDescription>
            </DialogHeader>
            {selectedStudent && (
              <div className="space-y-5 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <KV label="Student ID" value={selectedStudent.student_id} mono />
                  <KV label="Created" value={selectedStudent.created_at ? fmtDate(selectedStudent.created_at) : '—'} />
                  <KV label="Email" value={selectedStudent.email} />
                  <KV label="Phone" value={selectedStudent.phone} />
                  <KV label="Date of birth" value={selectedStudent.dob} />
                  <KV label="Marital status" value={selectedStudent.marital_status} />
                  <KV label="Student status" value={(selectedStudent.student_status || '').toString().toUpperCase()} />
                  <KV label="High school country" value={selectedStudent.high_school_country} />
                </div>

                {(selectedStudent.emergency_contact_name || selectedStudent.emergency_contact_phone) && (
                  <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-4">
                    <h4 className="font-semibold text-rose-800 text-xs uppercase tracking-wider mb-2">Emergency contact</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <KV label="Name" value={selectedStudent.emergency_contact_name} />
                      <KV label="Relation" value={selectedStudent.emergency_contact_relation} />
                      <KV label="Phone" value={selectedStudent.emergency_contact_phone} />
                    </div>
                  </div>
                )}

                <div className="bg-tge-cream-soft/40 border border-tge-navy/5 rounded-xl p-4">
                  <h4 className="font-semibold text-tge-navy text-xs uppercase tracking-wider mb-2">Application</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <KV label="Preferred programs" value={(selectedStudent.preferred_programs || []).join(', ')} />
                    <KV label="Preferred colleges" value={(selectedStudent.preferred_colleges || []).length + ' selected'} />
                    <KV label="Field of study" value={selectedStudent.field_of_study} />
                    <KV label="Assigned program" value={selectedStudent.assigned_program} />
                  </div>
                </div>

                {(() => {
                  const q = selectedStudent.qualifications;
                  const summary = typeof q === 'string' ? q : q?.summary;
                  if (!summary) return null;
                  return (
                    <div>
                      <h4 className="font-semibold text-tge-navy text-xs uppercase tracking-wider mb-1">Qualifications</h4>
                      <p className="text-tge-navy/80 whitespace-pre-wrap">{summary}</p>
                    </div>
                  );
                })()}

                <div>
                  <h4 className="font-semibold text-tge-navy text-xs uppercase tracking-wider mb-2">Documents on file</h4>
                  <RecruiterStudentManage
                    student={selectedStudent}
                    onUpdated={(fresh) => {
                      setSelectedStudent(fresh);
                      setStudents((list) => list.map((s) => (s.student_id === fresh.student_id ? { ...s, ...fresh } : s)));
                    }}
                    onDeleted={(sid) => {
                      setSelectedStudent(null);
                      setStudents((list) => list.filter((s) => s.student_id !== sid));
                    }}
                  />
                </div>

                {['loa_received', 'student_aid_started', 'noa_received', 'enrollment_contract_signed', 'invoice_raised_to_college', 'commission_received'].includes(selectedStudent.pipeline_stage) && (
                  <BookAppointment studentId={selectedStudent.student_id} />
                )}

                {selectedStudent.pipeline_stage === 'commission_received' && (
                  <RecruiterInvoiceUpload studentId={selectedStudent.student_id} />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
}

function MyCommissionsSection({ commissions, summary }) {
  const buckets = summary?.by_currency || {};
  const currencies = Object.keys(buckets);
  const [invoiceStudent, setInvoiceStudent] = useState(null);

  return (
    <motion.section
      variants={fadeUp}
      initial="hidden"
      animate="show"
      custom={4}
      className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden"
      data-testid="recruiter-my-commissions"
    >
      <div className="px-6 py-5 border-b border-tge-navy/5">
        <h3 className="font-serif-display text-xl text-tge-navy">My commissions</h3>
        <p className="text-xs text-tge-muted mt-1">
          Live, per-student breakdown based on each student's assigned program.
        </p>
      </div>

      {/* Single lifetime total per currency */}
      {currencies.length > 0 && (
        <div className="px-6 py-5 border-b border-tge-navy/5 space-y-4">
          {currencies.map((cur) => {
            const total = sumPayouts(commissions, cur);
            return (
              <div key={cur} className="grid grid-cols-1 gap-3">
                <KpiCard
                  tone="navy"
                  label={`Total commission (${cur})`}
                  value={`${cur} ${total.toLocaleString()}`}
                  hint="Across all your students"
                />
              </div>
            );
          })}
        </div>
      )}

      {commissions.length === 0 ? (
        <div className="text-center py-12 text-tge-muted text-sm">
          <DollarSign className="mx-auto text-tge-cream" size={32} />
          <p className="mt-3">No commission rows yet. Once your students are matched to programs they'll appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-tge-cream-soft">
              <tr>
                {['Student', 'Program', 'Tuition', 'Status', 'Your commission', ''].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-tge-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-tge-cream">
              {commissions.map((row) => {
                const c = row.commission;
                if (!c) {
                  return (
                    <tr key={row.student_id} className="hover:bg-tge-cream-soft/50 transition">
                      <td className="px-6 py-4 text-sm text-tge-navy font-medium">{row.student_name}</td>
                      <td className="px-6 py-4 text-sm text-tge-muted italic" colSpan={5}>
                        Awaiting program assignment by TGE admin.
                      </td>
                    </tr>
                  );
                }
                const cur = c.currency || '';
                const tuition = c.tuition_fee != null ? `${cur} ${Number(c.tuition_fee).toLocaleString()}` : '—';
                const canUploadInvoice = row.pipeline_stage === 'commission_received';
                return (
                  <tr key={row.student_id} className="hover:bg-tge-cream-soft/50 transition" data-testid={`commission-row-${row.student_id}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm text-tge-navy font-medium">{row.student_name}</div>
                      <div className="text-[11px] text-tge-muted">{row.student_id}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="text-tge-gold" size={14} />
                        <div>
                          <div>{c.program_name}</div>
                          <div className="text-[11px] text-tge-muted">{c.college_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">{tuition}</td>
                    <td className="px-6 py-4">
                      {row.pipeline_stage
                        ? <StagePill stage={row.pipeline_stage} testId={`commission-stage-${row.student_id}`} />
                        : <span className="text-xs text-tge-navy/40">Not started</span>}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className="font-serif-display text-emerald-700 text-lg cursor-help"
                        title={c.explain?.tge_to_recruiter}
                      >
                        {cur} {Number(c.tge_to_recruiter_amount).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canUploadInvoice && (
                        <button
                          type="button"
                          onClick={() => setInvoiceStudent(row)}
                          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
                          data-testid={`upload-invoice-btn-${row.student_id}`}
                        >
                          <FilePlus size={12} /> Upload Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice upload dialog */}
      <Dialog open={!!invoiceStudent} onOpenChange={(o) => !o && setInvoiceStudent(null)}>
        <DialogContent className="max-w-xl" data-testid="commission-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Upload invoice — {invoiceStudent?.student_name}</DialogTitle>
            <DialogDescription>
              Submit your commission invoice for this student. The TGE admin team will be notified immediately.
            </DialogDescription>
          </DialogHeader>
          {invoiceStudent && <RecruiterInvoiceUpload studentId={invoiceStudent.student_id} />}
        </DialogContent>
      </Dialog>
    </motion.section>
  );
}

function sumPayouts(rows, currency) {
  return rows
    .filter((r) => r.commission?.currency === currency)
    .reduce((sum, r) => sum + Number(r.commission?.tge_to_recruiter_amount || 0), 0);
}

/**
 * Read-only view of the commission structure TGE admin/staff has set for
 * this recruiter. Backend derives the recruiter_id from the session so no
 * other recruiter's numbers are ever leaked here.
 */
function MyCommissionStructureSection({ rows }) {
  return (
    <motion.section
      variants={fadeUp}
      initial="hidden"
      animate="show"
      custom={5}
      className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden"
      data-testid="recruiter-commission-structure"
    >
      <div className="px-6 py-5 border-b border-tge-navy/5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif-display text-xl text-tge-navy">My commission structure</h3>
          <p className="text-xs text-tge-muted mt-1">
            Per-program agreement set by TGE. To change these numbers, contact your TGE representative.
          </p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-tge-gold bg-tge-cream-soft border border-tge-gold/20 px-2 py-1 rounded-full">
          Read-only
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-12 text-tge-muted text-sm px-6">
          <DollarSign className="mx-auto text-tge-cream" size={32} />
          <p className="mt-3">
            No custom commission structure has been set for you yet — TGE will apply the
            default program-level commission until your bespoke rates are added.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-tge-cream-soft">
              <tr>
                {['College', 'Program', 'Tuition', 'Your commission'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-tge-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-tge-cream">
              {rows.map((r) => {
                const cur = r.currency || '';
                const tuition = r.tuition_fee != null ? `${cur} ${Number(r.tuition_fee).toLocaleString()}` : '—';
                const commissionDisplay =
                  r.commission_type === 'percentage'
                    ? `${Number(r.commission_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
                    : `${cur} ${Number(r.commission_value).toLocaleString()}`;
                return (
                  <tr
                    key={`${r.college_id}-${r.program_name}`}
                    className="hover:bg-tge-cream-soft/50 transition"
                    data-testid={`structure-row-${r.college_id}-${r.program_name}`}
                  >
                    <td className="px-6 py-4 text-sm text-tge-navy font-medium">
                      {r.college_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="text-tge-gold" size={14} />
                        <div>
                          <div>{r.program_name}</div>
                          {r.program_duration && (
                            <div className="text-[11px] text-tge-muted">{r.program_duration}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">{tuition}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="font-serif-display text-emerald-700 text-lg">
                        {commissionDisplay}
                      </span>
                      <span className="text-[11px] text-tge-muted ml-2 uppercase">
                        {r.commission_type}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.section>
  );
}

function KpiCard({ tone, label, value, hint }) {
  const ring = tone === 'amber' ? 'ring-amber-300' : tone === 'emerald' ? 'ring-emerald-300' : 'ring-tge-navy/15';
  const text = tone === 'amber' ? 'text-amber-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-tge-navy';
  return (
    <div className={`rounded-2xl bg-white p-4 ring-1 ${ring}`}>
      <div className="text-[10px] uppercase tracking-wider text-tge-muted font-semibold">{label}</div>
      <div className={`font-serif-display text-2xl mt-1 ${text}`}>{value}</div>
      {hint && <div className="text-[11px] text-tge-muted mt-1">{hint}</div>}
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-tge-cream pb-2 last:border-0">
      <span className="text-tge-muted text-xs uppercase tracking-wider">{label}</span>
      <span className="text-tge-navy">{value}</span>
    </div>
  );
}

function buildSeries(items, days) {
  const out = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push({
      key: d.toISOString().slice(0, 10),
      date: d.toLocaleDateString('en-CA', { timeZone: 'America/Vancouver', month: 'short', day: 'numeric' }),
      Students: 0,
    });
  }
  for (const it of items) {
    const k = (it.created_at || '').slice(0, 10);
    const row = out.find((r) => r.key === k);
    if (row) row.Students += 1;
  }
  return out;
}


function KV({ label, value, mono = false }) {
  const display = value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div>
      <div className="text-[10px] font-semibold text-tge-navy/50 uppercase tracking-wider">{label}</div>
      <div className={`text-tge-navy ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{display}</div>
    </div>
  );
}
